/**
 * CotizacionesDashboardView
 * ─────────────────────────
 * Vista unificada del Dashboard /admin que combina:
 *   • Dashboard buró (Peicar) — comportamiento heredado de CotizacionesBuroView
 *   • Pool Trim              — comportamiento heredado de CotizacionesView
 *
 * Modo seleccionado vía chips [Todas] [Peicar] [Trim]:
 *   - null  → buró API, todas las cotizaciones con buró (Pei + Trim).
 *   - 'pei' → buró API + filtro source=pei.
 *   - 'trim'→ Pool API (fetchCotizaciones) con todas las Trim,
 *             toggle Calificadas para filtrar las que ya tienen buró.
 *
 * Reglas:
 *   • Etiqueta TRIM en fila cuando source === 'trim' (ya en CotizacionRow).
 *   • Botón Asignar visible solo si source === 'trim', sin advisor y user es admin.
 *   • Click sobre fila abre DashboardWrapper solo en modo no-Trim
 *     (Trim sin buró no tiene reporte que mostrar).
 */
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { fetchCotizacionesBuro } from '../../../services/cotizacionesBuroApi';
import {
    fetchCotizaciones,
    assignCotizacionAdvisor,
    rejectCotizacion,
    unrejectCotizacion,
    toggleHideCotizacion,
    changeCotizacionSource,
    fetchVendedores,
} from '../../../services/cotizacionesApi';
import CotizacionRow from './CotizacionRow';
import AssignButton from './AssignButton';
import RejectModal from './RejectModal';
import DashboardHero from './DashboardHero';
import ToolbarFilters from './ToolbarFilters';
import { CotizacionRowSkeleton } from '../../../Components';
import {
    cotizacionesCSS,
    quoteAdminActionsCSS,
} from './Cotizaciones.css';
import usePermissions from '../../../Hooks/usePermissions';
import { AuthContext } from '../../../Context/AuthContext';

const NONE_VENDOR_KEY = 'none';

const BURO_STATUS_FILTERS = [
    { key: null,           label: 'Todas' },
    { key: 'docs_pending', label: 'Faltan docs' },
    { key: 'analysis',     label: 'Análisis' },
    { key: 'ocular',       label: 'Visita ocular' },
    { key: 'quoting',      label: 'En cotización' },
    { key: 'paid',         label: 'Pago inicial' },
    { key: 'invoiced',     label: 'Orden de compra' },
    { key: 'contract_step',label: 'Contrato' },
    { key: 'insurance',    label: 'Seguros' },
    { key: 'plates',       label: 'Placas' },
    { key: 'rejected',     label: 'Rechazados' },
    { key: 'approved',     label: 'Aprobado' },
    { key: 'hidden',       label: 'Ocultas' },
];

const TRIM_PAGE_KPI_KEYS = {
    total: 0,
    in_progress: 0,
    approved: 0,
    rejected: 0,
    docs_pending: 0,
    cancelled: 0,
};

const BURO_PAGE_KPI_KEYS = {
    total: 0,
    docs_pending: 0,
    analysis: 0,
    ocular: 0,
    quoting: 0,
    paid: 0,
    invoiced: 0,
    contract_step: 0,
    insurance: 0,
    rejected: 0,
    approved: 0,
};

const axiosCancelled = (err) =>
    err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED' || err?.message === 'canceled';

const CotizacionesDashboardView = ({ toast, onSelectPerson }) => {
    const { isAdminPei, isAdminTrim, isVendedorPei, isVendedorTrim } = usePermissions();
    const { authInfo } = useContext(AuthContext) ?? { authInfo: {} };
    const adminPei = isAdminPei();
    const adminTrim = isAdminTrim();
    const vendedorPei = isVendedorPei();
    const vendedorTrim = isVendedorTrim();
    const isAdmin = adminPei || adminTrim;
    const isVendedor = vendedorPei || vendedorTrim;
    const lockedToTrim = adminTrim || vendedorTrim;
    const canAssign = isAdmin;
    const showVendorChip = isAdmin;
    const showAdminActions = isAdmin;

    const [rows, setRows] = useState([]);
    const [kpis, setKpis] = useState({});
    const [vendors, setVendors] = useState([]);
    const [allVendors, setAllVendors] = useState([]);
    const [meta, setMeta] = useState({ current_page: 1, last_page: 1, total: 0 });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const [sourceFilter, setSourceFilter] = useState(lockedToTrim ? 'trim' : null);   // null | 'pei' | 'trim'
    const [statusFilter, setStatusFilter] = useState(null);
    const [vendorFilter, setVendorFilter] = useState(null);
    const [calificadas, setCalificadas] = useState(lockedToTrim);
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);

    const [searchInput, setSearchInput] = useState('');
    const [rejectModal, setRejectModal] = useState(null); // { budgetId, rowId } | null
    const [rejecting, setRejecting] = useState(false);
    const [assigningRowId, setAssigningRowId] = useState(null);
    const [unrejectingRowId, setUnrejectingRowId] = useState(null);
    const [hidingRowId, setHidingRowId] = useState(null);
    const [changingSourceRowId, setChangingSourceRowId] = useState(null);
    const debounceRef = useRef(null);

    const isTrimMode = sourceFilter === 'trim';

    const buroParams = useMemo(() => ({
        page,
        search,
        status:    statusFilter,
        advisorId: vendorFilter,
        source:    sourceFilter, // 'pei' | null
    }), [page, search, statusFilter, vendorFilter, sourceFilter]);

    const trimParams = useMemo(() => {
        const p = { page, source: 'trim' };
        if (calificadas) p.has_buro = 1;
        if (statusFilter) p.status = statusFilter;
        if (search) p.search = search;
        if (vendorFilter !== null && vendorFilter !== undefined) p.advisor_id = vendorFilter;
        return p;
    }, [page, calificadas, statusFilter, vendorFilter, search]);

    const loadBuro = useCallback(async (controller) => {
        const res = await fetchCotizacionesBuro(buroParams, { signal: controller?.signal });
        setRows(res.rows ?? []);
        setKpis(res.kpis ?? {});
        if (res.vendors) setVendors(res.vendors);
        setMeta({
            current_page: res.meta?.current_page ?? 1,
            last_page:    res.meta?.last_page ?? 1,
            total:        res.meta?.total ?? 0,
        });
    }, [buroParams]);

    const loadTrim = useCallback(async (controller) => {
        const res = await fetchCotizaciones(trimParams, { signal: controller?.signal });
        setRows(res.data ?? []);
        setKpis(res.meta?.kpis ?? {});
        if (res.meta?.vendors) setVendors(res.meta.vendors);
        setMeta({
            current_page: res.meta?.current_page ?? 1,
            last_page:    res.meta?.last_page ?? 1,
            total:        res.meta?.total ?? 0,
        });
    }, [trimParams]);

    const load = useCallback(async (controller) => {
        setLoading(true);
        setError(null);
        try {
            if (isTrimMode) {
                await loadTrim(controller);
            } else {
                await loadBuro(controller);
            }
        } catch (err) {
            if (axiosCancelled(err)) return;
            console.error('CotizacionesDashboardView fetch error', err);
            const msg = err?.response?.data?.message ?? 'No se pudieron cargar las cotizaciones.';
            setError(msg);
            toast?.current?.show?.({ severity: 'error', summary: 'Error', detail: msg, life: 4000 });
        } finally {
            if (!controller?.signal?.aborted) {
                setLoading(false);
            }
        }
    }, [isTrimMode, loadBuro, loadTrim, toast]);

    useEffect(() => {
        const controller = new AbortController();
        load(controller);
        return () => controller.abort();
    }, [load]);

    // Todos los vendedores activos (Pei + Trim) para filtros y asignación.
    useEffect(() => {
        if (!canAssign) return undefined;
        const controller = new AbortController();
        fetchVendedores({ signal: controller.signal })
            .then((res) => setAllVendors(res?.data ?? []))
            .catch((err) => { if (!axiosCancelled(err)) console.error('fetchVendedores error', err); });
        return () => controller.abort();
    }, [canAssign]);

    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchInput(value);
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => { setPage(1); setSearch(value.trim()); }, 280);
    };

    const pageKpis = useMemo(() => {
        const out = isTrimMode ? { ...TRIM_PAGE_KPI_KEYS } : { ...BURO_PAGE_KPI_KEYS };
        out.total = rows.length;
        for (const r of rows) {
            if (r.status && out[r.status] !== undefined) out[r.status] += 1;
        }
        return out;
    }, [rows, isTrimMode]);

    const handleSourceFilter = (src) => {
        setPage(1);
        setSourceFilter(src);
        setStatusFilter(null);
        setVendorFilter(null);
        setCalificadas(false);
    };
    const handleStatusFilter = (filter) => { setPage(1); setStatusFilter(filter ?? null); };
    const handleVendorFilter = (vendorId) => { setPage(1); setVendorFilter(vendorId ?? null); };
    const toggleCalificadas  = () => { setPage(1); setCalificadas((v) => !v); };

    const handleAssign = async (rowId, budgetId, advisorId) => {
        if (!budgetId) {
            toast?.current?.show?.({ severity: 'warn', summary: 'Sin cotización', detail: 'Este usuario no tiene un presupuesto asociado.', life: 4000 });
            return;
        }
        try {
            setAssigningRowId(rowId);
            const res = await assignCotizacionAdvisor(budgetId, advisorId);
            const newAdvisor = res?.data?.advisor ?? null;
            setRows((prev) => prev.map((r) => r.id === rowId ? { ...r, advisor: newAdvisor } : r));
            toast?.current?.show?.({
                severity: 'success',
                summary: advisorId ? 'Cotización asignada' : 'Asignación removida',
                detail: advisorId ? `Asignada a ${newAdvisor?.name ?? 'vendedor'}.` : 'La cotización quedó sin vendedor.',
                life: 3000,
            });
            if (vendorFilter === NONE_VENDOR_KEY && advisorId) load();
        } catch (err) {
            const msg = err?.response?.data?.message ?? 'No se pudo asignar la cotización.';
            toast?.current?.show?.({ severity: 'error', summary: 'Error al asignar', detail: msg, life: 4000 });
        } finally {
            setAssigningRowId(null);
        }
    };

    const mergeRejectedRow = (row, updated) => {
        if (!updated) return row;
        if (isTrimMode) return updated;
        return {
            ...row,
            ...updated,
            id: row.id,
            budget_id: row.budget_id ?? updated.id,
            _raw: row._raw ? {
                ...row._raw,
                is_rejected: updated.is_rejected,
                has_quote_process: updated.has_quote_process,
                action_detail: updated.action_detail,
            } : row._raw,
        };
    };

    const handleReject = async (comment) => {
        if (!rejectModal?.budgetId || rejecting) return;
        try {
            setRejecting(true);
            const updated = await rejectCotizacion(rejectModal.budgetId, comment);
            setRows((prev) => prev.map((r) => (
                r.id === rejectModal.rowId ? mergeRejectedRow(r, updated) : r
            )));
            setRejectModal(null);
            toast?.current?.show?.({ severity: 'success', summary: 'Cotización rechazada', detail: 'El rechazo fue registrado.', life: 3000 });
        } catch (err) {
            const msg = err?.response?.data?.message ?? 'No se pudo rechazar la cotización.';
            toast?.current?.show?.({ severity: 'error', summary: 'Error al rechazar', detail: msg, life: 4000 });
        } finally {
            setRejecting(false);
        }
    };

    const handleUnreject = async (budgetId, rowId) => {
        if (!budgetId) return;
        try {
            setUnrejectingRowId(rowId);
            const updated = await unrejectCotizacion(budgetId);
            if (statusFilter === 'rejected') {
                setRows((prev) => prev.filter((r) => r.id !== rowId));
                setMeta((prev) => ({ ...prev, total: Math.max(0, (prev.total ?? 0) - 1) }));
            } else {
                setRows((prev) => prev.map((r) => (
                    r.id === rowId ? mergeRejectedRow(r, updated) : r
                )));
            }
            toast?.current?.show?.({ severity: 'success', summary: 'Cotización desrechazada', detail: 'La cotización volvió al flujo normal.', life: 3000 });
        } catch (err) {
            const msg = err?.response?.data?.message ?? 'No se pudo desrechazar la cotización.';
            toast?.current?.show?.({ severity: 'error', summary: 'Error', detail: msg, life: 4000 });
        } finally {
            setUnrejectingRowId(null);
        }
    };

    const handleChangeSource = async (budgetId, rowId, newSource) => {
        if (!budgetId) {
            toast?.current?.show?.({ severity: 'warn', summary: 'Sin cotización', detail: 'Este usuario no tiene un presupuesto asociado.', life: 4000 });
            return;
        }
        try {
            setChangingSourceRowId(rowId);
            const updated = await changeCotizacionSource(budgetId, newSource);
            const filterMismatch = (sourceFilter === 'pei' && newSource !== 'pei')
                || (sourceFilter === 'trim' && newSource !== 'trim')
                || (isTrimMode && newSource !== 'trim');
            if (filterMismatch) {
                setRows((prev) => prev.filter((r) => r.id !== rowId));
                setMeta((prev) => ({ ...prev, total: Math.max(0, (prev.total ?? 0) - 1) }));
            } else {
                setRows((prev) => prev.map((r) => (
                    r.id === rowId ? mergeRejectedRow(r, updated) : r
                )));
            }
            toast?.current?.show?.({
                severity: 'success',
                summary: 'Cotización movida',
                detail: `Ahora pertenece a ${newSource === 'trim' ? 'Trim' : 'Peicar'}.`,
                life: 3000,
            });
        } catch (err) {
            const msg = err?.response?.data?.message ?? 'No se pudo cambiar el origen de la cotización.';
            toast?.current?.show?.({ severity: 'error', summary: 'Error', detail: msg, life: 4000 });
        } finally {
            setChangingSourceRowId(null);
        }
    };

    const handleToggleHide = async (budgetId, rowId) => {
        if (!budgetId) {
            toast?.current?.show?.({ severity: 'warn', summary: 'Sin cotización', detail: 'Este usuario no tiene un presupuesto asociado.', life: 4000 });
            return;
        }
        try {
            setHidingRowId(rowId);
            const res = await toggleHideCotizacion(budgetId);
            setRows((prev) => prev.filter((r) => r.id !== rowId));
            setMeta((prev) => ({ ...prev, total: Math.max(0, (prev.total ?? 0) - 1) }));
            toast?.current?.show?.({
                severity: 'success',
                summary: res?.is_hidden ? 'Cotización ocultada' : 'Cotización visible',
                detail: res?.is_hidden ? 'La cotización salió de la vista actual.' : 'La cotización volvió a la vista principal.',
                life: 3000,
            });
        } catch (err) {
            const msg = err?.response?.data?.message ?? 'No se pudo actualizar la visibilidad.';
            toast?.current?.show?.({ severity: 'error', summary: 'Error', detail: msg, life: 4000 });
        } finally {
            setHidingRowId(null);
        }
    };

    const peiVendors = allVendors.filter((v) => v.source === 'pei');
    const trimVendors = allVendors.filter((v) => v.source === 'trim');

    const renderRowExtra = (row) => {
        if (!canAssign && !showAdminActions) return null;
        const showChangeSource = adminPei;

        const budgetId = row?.budget_id ?? row?.id;
        const isTrim = row?.source === 'trim';
        const vendorList = isTrim ? trimVendors : peiVendors;
        const title = isTrim ? 'Asignar a Vendedor Trim' : 'Asignar a Vendedor Peicar';

        return (
            <div css={quoteAdminActionsCSS()}>
                {canAssign && (
                    <AssignButton
                        title={title}
                        currentAdvisorId={row?.advisor?.id ?? null}
                        vendors={vendorList}
                        loading={assigningRowId === row.id}
                        onAssign={(advisorId) => handleAssign(row.id, budgetId, advisorId)}
                    />
                )}
                {showAdminActions && (
                    <>
                        {row.is_rejected && !row.has_quote_process ? (
                            <button
                                type="button"
                                className="qa-btn qa-btn-neutral"
                                disabled={unrejectingRowId === row.id}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleUnreject(budgetId, row.id);
                                }}
                            >
                                <i className={unrejectingRowId === row.id ? 'pi pi-spin pi-spinner' : 'pi pi-undo'} />
                                Desrechazar
                            </button>
                        ) : (
                            <button
                                type="button"
                                className="qa-btn qa-btn-destructive"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setRejectModal({ budgetId, rowId: row.id, quote: row });
                                }}
                            >
                                <i className="pi pi-times-circle" />
                                Rechazar
                            </button>
                        )}
                        {showChangeSource && (row.source === 'pei' || row.source === 'trim') && (
                            <button
                                type="button"
                                className="qa-btn qa-btn-neutral"
                                disabled={changingSourceRowId === row.id}
                                title={row.source === 'pei' ? 'Mover esta cotización a Trim' : 'Mover esta cotización a Peicar'}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleChangeSource(budgetId, row.id, row.source === 'pei' ? 'trim' : 'pei');
                                }}
                            >
                                <i className={changingSourceRowId === row.id ? 'pi pi-spin pi-spinner' : 'pi pi-arrow-right-arrow-left'} />
                                {row.source === 'pei' ? 'Mover a Trim' : 'Mover a Pei'}
                            </button>
                        )}
                        <button
                            type="button"
                            className="qa-btn qa-btn-neutral"
                            disabled={hidingRowId === row.id}
                            onClick={(e) => {
                                e.stopPropagation();
                                handleToggleHide(budgetId, row.id);
                            }}
                        >
                            <i className={hidingRowId === row.id ? 'pi pi-spin pi-spinner' : (row.is_hidden ? 'pi pi-eye' : 'pi pi-eye-slash')} />
                            {row.is_hidden ? 'Mostrar' : 'Ocultar'}
                        </button>
                    </>
                )}
            </div>
        );
    };

    const emptyMsg = isTrimMode && calificadas
        ? 'No hay cotizaciones Trim calificadas (con buró) que mostrar.'
        : isTrimMode
        ? 'No hay cotizaciones Trim que mostrar.'
        : 'No hay cotizaciones que mostrar.';

    const heroTotal = meta?.total ?? kpis?.total ?? rows.length;
    const heroPipeline = useMemo(
        () => rows.reduce((s, r) => s + (Number(r.price) || 0), 0),
        [rows],
    );
    // Si vendorFilter es un id, busca el name; si es NONE_VENDOR_KEY o null, pasa null al hero.
    const heroVendorName = useMemo(() => {
        if (vendorFilter === null || vendorFilter === undefined) return null;
        if (vendorFilter === NONE_VENDOR_KEY) return null;
        const v = allVendors.find((vv) => String(vv.id) === String(vendorFilter));
        return v?.name ?? null;
    }, [vendorFilter, allVendors]);

    return (
        <div css={cotizacionesCSS()}>
            <DashboardHero
                total={heroTotal}
                pipelineMXN={heroPipeline}
                vendorFilter={heroVendorName}
                userName={authInfo?.user?.name ?? 'Admin'}
                searchValue={searchInput}
                onSearchChange={handleSearchChange}
                loading={loading}
                lastUpdatedLabel={null}
                searchPlaceholder={isTrimMode
                    ? 'Buscar por nombre o correo'
                    : 'Buscar por nombre, correo o unidad'}
            />

            <ToolbarFilters
                statusFilter={statusFilter}
                onStatusChange={handleStatusFilter}
                statusOptions={BURO_STATUS_FILTERS}
                vendorFilter={vendorFilter}
                onVendorChange={handleVendorFilter}
                vendorOptions={showVendorChip ? (lockedToTrim ? trimVendors : allVendors) : []}
                sourceFilter={sourceFilter}
                onSourceChange={handleSourceFilter}
                showSourceChip={adminPei}
                isTrimMode={isTrimMode}
                calificadas={calificadas}
                onCalificadasChange={toggleCalificadas}
            />

            {loading ? (
                <CotizacionRowSkeleton count={5} />
            ) : error && rows.length === 0 ? (
                <div className="cot-empty">
                    <i className="pi pi-exclamation-triangle" />
                    {error}
                </div>
            ) : rows.length === 0 ? (
                <div className="cot-empty">
                    <i className="pi pi-inbox" />
                    {emptyMsg}
                </div>
            ) : (
                <>
                    <div className="cot-eyebrow">
                        <span className="cot-eyebrow-title">Cotizaciones</span>
                        <span className="cot-eyebrow-slashes">/ / /</span>
                        <div className="cot-eyebrow-rule" />
                        <span className="cot-eyebrow-count">{meta.total} resultados</span>
                    </div>
                    <div className="cot-hairline-tri" />
                    <div className="cot-row-header">
                        <div className="cot-row-header-cell">Cliente</div>
                        <div className="cot-row-header-cell">Unidad</div>
                        <div className="cot-row-header-cell">Estado</div>
                        <div className="cot-row-header-cell align-right">Monto</div>
                        <div className="cot-row-header-cell align-center">Progreso</div>
                    </div>
                    <div className="cot-list">
                        {rows.map((row) => (
                            <div key={row.id} className="cot-list-card">
                                <CotizacionRow
                                    row={row}
                                    onClick={!isTrimMode && onSelectPerson && row._raw
                                        ? () => onSelectPerson(row._raw)
                                        : undefined}
                                    extraActions={renderRowExtra(row)}
                                />
                            </div>
                        ))}
                    </div>
                </>
            )}

            {meta.last_page > 1 && (
                <div className="cot-pagination">
                    <span>
                        Página {meta.current_page} de {meta.last_page} · {meta.total} cotizaciones
                    </span>
                    <span style={{ display: 'flex', gap: '0.4rem' }}>
                        <button
                            type="button"
                            disabled={meta.current_page <= 1 || loading}
                            onClick={() => setPage((p) => Math.max(1, p - 1))}
                        >
                            Anterior
                        </button>
                        <button
                            type="button"
                            disabled={meta.current_page >= meta.last_page || loading}
                            onClick={() => setPage((p) => p + 1)}
                        >
                            Siguiente
                        </button>
                    </span>
                </div>
            )}

            <RejectModal
                visible={!!rejectModal}
                quote={rejectModal?.quote}
                onConfirm={handleReject}
                onCancel={() => { if (!rejecting) setRejectModal(null); }}
                loading={rejecting}
            />
        </div>
    );
};

export default CotizacionesDashboardView;
